import { useId, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Phone: a bottom sheet. sm+: a centred dialog. */
export function Sheet({
  title,
  onClose,
  className,
  children,
}: {
  title: string
  onClose: () => void
  /** Width from sm up; defaults to sm:max-w-sm. */
  className?: string
  children: ReactNode
}) {
  const titleId = useId()
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        className={cn(
          'max-h-[92vh] w-full overflow-y-auto rounded-t-[20px] bg-[var(--fl-surface)] px-5 pt-2 pb-[max(24px,env(safe-area-inset-bottom))] shadow-[rgba(0,0,0,0.15)_0px_8px_40px] motion-safe:animate-fade-slide-up sm:mx-4 sm:max-w-sm sm:rounded-[20px] sm:p-6',
          className
        )}
      >
        <div
          aria-hidden="true"
          className="mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--fl-border)] sm:hidden"
        />
        <div className="mb-4 flex items-center justify-between">
          <h2 id={titleId} className="text-lg font-bold text-[var(--fl-text-1)]">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)] sm:-mr-2"
          >
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}
